import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Save, Printer, ExternalLink, Copy, ArrowRightCircle, Mail, Package } from "lucide-react";
import ProductPicker from "@/components/ProductPicker";

const STATUS_META = {
  draft:     { label: "Draft",     color: "#94A3B8", bg: "rgba(148,163,184,0.15)" },
  sent:      { label: "Sent",      color: "#38BDF8", bg: "rgba(56,189,248,0.15)" },
  accepted:  { label: "Accepted",  color: "#A78BFA", bg: "rgba(167,139,250,0.15)" },
  converted: { label: "Converted", color: "#25D366", bg: "rgba(37,211,102,0.15)" },
  rejected:  { label: "Rejected",  color: "#F87171", bg: "rgba(248,113,113,0.15)" },
  expired:   { label: "Expired",   color: "#6B7280", bg: "rgba(107,114,128,0.15)" },
};
const fmt = (n, ccy) => new Intl.NumberFormat(undefined, { style: "currency", currency: ccy || "SGD" }).format(n || 0);
const todayIso = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

export default function QuotationEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preDealId = searchParams.get("deal_id");
  const qc = useQueryClient();

  const { data: profile } = useQuery({ queryKey: ["billing-profile"], queryFn: () => api.get("/billing-profile").then((r) => r.data) });
  const { data: existing } = useQuery({
    queryKey: ["quotation", id],
    queryFn: () => api.get(`/quotations/${id}`).then((r) => r.data),
    enabled: !isNew,
  });
  const { data: deals = [] } = useQuery({ queryKey: ["deals-simple"], queryFn: () => api.get("/deals").then((r) => r.data) });

  const [form, setForm] = useState(() => ({
    bill_to: { name: "", address: "", postal_code: "", phone: "", email: "" },
    issue_date: todayIso(),
    valid_until: addDays(todayIso(), 30),
    currency: "SGD",
    line_items: [{ description: "", quantity: 1, price: 0, include_gst: true, include_service_fee: true, product_id: null }],
    gst_rate: 0.09,
    service_fee_pct: null,  // null = inherit from billing profile default
    terms: [],
    notes: "",
    status: "draft",
    deal_id: null,
    contact_id: null,
  }));

  useEffect(() => { if (existing) setForm({ ...existing }); }, [existing]);
  useEffect(() => {
    if (isNew && profile && (!form.terms || form.terms.length === 0)) {
      setForm((f) => ({ ...f, terms: profile.default_terms || [] }));
    }
    // inherit service fee default from billing profile for new quotes
    if (isNew && profile && form.service_fee_pct == null && profile.default_service_fee_pct != null) {
      setForm((f) => ({ ...f, service_fee_pct: profile.default_service_fee_pct }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);
  useEffect(() => {
    if (isNew && preDealId && !form.deal_id) {
      setForm((f) => ({ ...f, deal_id: preDealId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preDealId]);

  const totals = useMemo(() => {
    let subtotal = 0, gst = 0, feeBase = 0;
    for (const li of form.line_items || []) {
      const amt = (Number(li.quantity) || 0) * (Number(li.price) || 0);
      subtotal += amt;
      if (li.include_gst) gst += amt * (form.gst_rate || 0);
      if (li.include_service_fee !== false) feeBase += amt;
    }
    const pct = Number(form.service_fee_pct) || 0;
    const fee = feeBase * (pct / 100);
    const total = subtotal + gst + fee;
    const round = (n) => Math.round(n * 100) / 100;
    return { subtotal: round(subtotal), gst: round(gst), fee: round(fee), total: round(total) };
  }, [form.line_items, form.gst_rate, form.service_fee_pct]);

  const meta = STATUS_META[form.status] || STATUS_META.draft;
  const locked = existing?.locked;

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = { ...form };
      ["subtotal", "gst_amount", "total", "public_slug", "quote_number", "id",
       "created_at", "updated_at", "created_by", "billing_profile_snapshot",
       "company_id", "division_id", "converted_invoice_id", "converted_invoice_number",
       "converted_at", "email_events", "locked"].forEach((k) => delete payload[k]);
      return isNew
        ? api.post("/quotations", payload).then((r) => r.data)
        : api.patch(`/quotations/${id}`, payload).then((r) => r.data);
    },
    onSuccess: (data) => {
      toast.success(isNew ? `Quotation ${data.quote_number} created` : "Saved");
      qc.invalidateQueries({ queryKey: ["quotations"] });
      qc.invalidateQueries({ queryKey: ["quotation", data.id] });
      if (isNew) navigate(`/quotations/${data.id}`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });

  const convertMut = useMutation({
    mutationFn: () => api.post(`/quotations/${id}/convert-to-invoice`).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(`Converted to invoice ${data.invoice.invoice_number}`);
      qc.invalidateQueries({ queryKey: ["quotation", id] });
      navigate(`/invoices/${data.invoice.id}`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Convert failed"),
  });

  const setStatusMut = useMutation({
    mutationFn: (status) => api.patch(`/quotations/${id}`, { status }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quotation", id] }),
  });

  const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { description: "", quantity: 1, price: 0, include_gst: true, include_service_fee: true, product_id: null }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, x) => x !== i) }));
  const setLine = (i, patch) => setForm((f) => ({ ...f, line_items: f.line_items.map((li, x) => x === i ? { ...li, ...patch } : li) }));
  const applyProductToLine = (i, product) => setLine(i, {
    product_id: product.id,
    description: product.description ? `${product.name} — ${product.description}` : product.name,
    price: product.unit_price,
    include_gst: product.include_gst !== false,
    include_service_fee: product.include_service_fee !== false,
  });

  const publicUrl = existing?.public_slug ? `${window.location.origin}/quote/${existing.public_slug}` : null;

  return (
    <div>
      <PageHeader
        overline={isNew ? "New" : existing?.quote_number || "Quotation"}
        title={isNew ? "New Quotation" : `Quotation ${existing?.quote_number || ""}`}
        subtitle={
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="rounded-sm text-[10px]" style={{ backgroundColor: meta.bg, color: meta.color, borderColor: meta.color + "44" }}>{meta.label}</Badge>
            {existing?.converted_invoice_number && (
              <Link to={`/invoices?search=${existing.converted_invoice_number}`} className="text-xs text-primary hover:underline" data-testid="quote-linked-invoice">
                Linked invoice: {existing.converted_invoice_number}
              </Link>
            )}
          </div>
        }
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {!isNew && publicUrl && (
              <Button variant="outline" className="rounded-sm" onClick={() => { navigator.clipboard.writeText(publicUrl).then(() => toast.success("Public URL copied")).catch(() => toast.error("Copy failed")); }} data-testid="quote-copy-url"><Copy className="h-4 w-4 mr-2" />Copy URL</Button>
            )}
            {!isNew && publicUrl && (
              <Button variant="outline" className="rounded-sm" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/public/quote/${existing?.public_slug}/pdf`, "_blank")} data-testid="quote-download-pdf"><Printer className="h-4 w-4 mr-2" />Download PDF</Button>
            )}
            {!isNew && publicUrl && (
              <Button variant="outline" className="rounded-sm" onClick={() => window.open(publicUrl, "_blank")} data-testid="quote-view-public"><ExternalLink className="h-4 w-4 mr-2" />Preview</Button>
            )}
            {!isNew && !locked && <SendQuotationButton quoteId={id} defaultTo={form.bill_to?.email} status={form.status} />}
            {!isNew && !locked && existing?.status !== "converted" && (
              <Button className="rounded-sm bg-green-500 hover:bg-green-500/90 text-black" onClick={() => convertMut.mutate()} disabled={convertMut.isPending} data-testid="quote-convert">
                <ArrowRightCircle className="h-4 w-4 mr-2" /> Convert to invoice
              </Button>
            )}
            {!isNew && !locked && (
              <Button className="rounded-sm bg-primary" onClick={() => saveMut.mutate()} disabled={saveMut.isPending} data-testid="quote-save"><Save className="h-4 w-4 mr-2" />Save</Button>
            )}
            {isNew && (
              <Button className="rounded-sm bg-primary" onClick={() => saveMut.mutate()} disabled={saveMut.isPending} data-testid="quote-save"><Save className="h-4 w-4 mr-2" />Create quotation</Button>
            )}
          </div>
        }
      />

      {locked && (
        <div className="mx-6 lg:mx-10 mt-4 rounded-sm border border-border bg-secondary/30 px-4 py-3 text-xs text-muted-foreground" data-testid="quote-locked-banner">
          This quotation is <b>{form.status}</b> and can no longer be edited.
          {existing?.converted_invoice_number && (
            <> Continue on the linked invoice <Link className="text-primary hover:underline" to="/invoices">{existing.converted_invoice_number}</Link>.</>
          )}
        </div>
      )}

      <div className="px-6 lg:px-10 pb-10 grid gap-6 lg:grid-cols-[1fr_360px] mt-6">
        <div className="space-y-6">
          <Card className="rounded-sm bg-card border-border p-6" data-testid="quote-bill-to-card">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3">Bill To</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Customer name</Label><Input disabled={locked} className="rounded-sm mt-1.5 bg-secondary/40" value={form.bill_to.name || ""} onChange={(e) => setForm((f) => ({ ...f, bill_to: { ...f.bill_to, name: e.target.value } }))} data-testid="quote-bill-name" /></div>
              <div className="col-span-2"><Label>Address</Label><Input disabled={locked} className="rounded-sm mt-1.5 bg-secondary/40" value={form.bill_to.address || ""} onChange={(e) => setForm((f) => ({ ...f, bill_to: { ...f.bill_to, address: e.target.value } }))} /></div>
              <div><Label>Postal code</Label><Input disabled={locked} className="rounded-sm mt-1.5 bg-secondary/40" value={form.bill_to.postal_code || ""} onChange={(e) => setForm((f) => ({ ...f, bill_to: { ...f.bill_to, postal_code: e.target.value } }))} /></div>
              <div><Label>Phone</Label><Input disabled={locked} className="rounded-sm mt-1.5 bg-secondary/40" value={form.bill_to.phone || ""} onChange={(e) => setForm((f) => ({ ...f, bill_to: { ...f.bill_to, phone: e.target.value } }))} /></div>
              <div className="col-span-2"><Label>Email</Label><Input disabled={locked} type="email" className="rounded-sm mt-1.5 bg-secondary/40" value={form.bill_to.email || ""} onChange={(e) => setForm((f) => ({ ...f, bill_to: { ...f.bill_to, email: e.target.value } }))} data-testid="quote-bill-email" /></div>
            </div>
          </Card>

          <Card className="rounded-sm bg-card border-border p-6">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Issue date</Label><Input disabled={locked} type="date" className="rounded-sm mt-1.5 bg-secondary/40" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
              <div><Label>Valid until</Label><Input disabled={locked} type="date" className="rounded-sm mt-1.5 bg-secondary/40" value={form.valid_until || ""} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="quote-valid-until" /></div>
              <div>
                <Label>Link to deal <span className="text-[10px] text-muted-foreground uppercase tracking-widest">— optional</span></Label>
                <Select disabled={locked} value={form.deal_id || "none"} onValueChange={(v) => setForm({ ...form, deal_id: v === "none" ? null : v })}>
                  <SelectTrigger className="rounded-sm mt-1.5 bg-secondary/40" data-testid="quote-deal-picker"><SelectValue placeholder="Standalone" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Standalone (no deal)</SelectItem>
                    {deals.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>GST rate</Label>
                <Input disabled={locked} type="number" step="0.01" className="rounded-sm mt-1.5 bg-secondary/40" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
          </Card>

          <Card className="rounded-sm bg-card border-border p-6" data-testid="quote-line-items">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Line items</div>
              {!locked && <Button variant="outline" size="sm" className="rounded-sm" onClick={addLine} data-testid="quote-add-line"><Plus className="h-4 w-4 mr-1.5" />Add line</Button>}
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-[36px_1fr_70px_90px_50px_50px_90px_28px] gap-2 text-[10px] uppercase tracking-widest text-muted-foreground font-bold px-1">
                <div />
                <div>Description</div><div>Qty</div><div>Price</div><div title="Include GST">GST</div><div title="Include in service fee">Fee</div><div className="text-right">Amount</div><div />
              </div>
              {form.line_items.map((li, i) => {
                const amt = (Number(li.quantity) || 0) * (Number(li.price) || 0);
                return (
                  <div key={i} className="grid grid-cols-[36px_1fr_70px_90px_50px_50px_90px_28px] gap-2 items-start" data-testid={`quote-line-${i}`}>
                    <div className="pt-1">
                      <ProductPicker
                        disabled={locked}
                        selectedId={li.product_id}
                        onPick={(p) => applyProductToLine(i, p)}
                        testId={`quote-line-product-picker-${i}`}
                      />
                    </div>
                    <Textarea disabled={locked} rows={2} className="rounded-sm bg-secondary/40" value={li.description} onChange={(e) => setLine(i, { description: e.target.value })} data-testid={`quote-line-desc-${i}`} />
                    <Input disabled={locked} type="number" step="0.01" className="rounded-sm bg-secondary/40" value={li.quantity} onChange={(e) => setLine(i, { quantity: parseFloat(e.target.value) || 0 })} />
                    <Input disabled={locked} type="number" step="0.01" className="rounded-sm bg-secondary/40" value={li.price} onChange={(e) => setLine(i, { price: parseFloat(e.target.value) || 0 })} />
                    <div className="flex items-center justify-center pt-2.5"><Checkbox disabled={locked} checked={!!li.include_gst} onCheckedChange={(v) => setLine(i, { include_gst: !!v })} data-testid={`quote-line-gst-${i}`} /></div>
                    <div className="flex items-center justify-center pt-2.5"><Checkbox disabled={locked} checked={li.include_service_fee !== false} onCheckedChange={(v) => setLine(i, { include_service_fee: !!v })} data-testid={`quote-line-fee-${i}`} /></div>
                    <div className="text-right pt-2.5 text-sm font-semibold">{fmt(amt, form.currency)}</div>
                    {!locked
                      ? <button onClick={() => removeLine(i)} className="pt-2.5 text-muted-foreground hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                      : <div />
                    }
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="rounded-sm bg-card border-border p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3">Terms &amp; Conditions</div>
            <Textarea disabled={locked} rows={6} className="rounded-sm bg-secondary/40" placeholder="One clause per line…" value={(form.terms || []).join("\n")} onChange={(e) => setForm({ ...form, terms: e.target.value.split("\n") })} data-testid="quote-terms" />
            <div className="mt-3"><Label>Notes</Label><Textarea disabled={locked} rows={3} className="rounded-sm mt-1.5 bg-secondary/40" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="rounded-sm bg-card border-border p-6" data-testid="quote-totals">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3">Totals</div>
            <div className="space-y-1.5 text-sm">
              <Row label="Subtotal" value={fmt(totals.subtotal, form.currency)} />
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <span>Service fee</span>
                  <Input
                    disabled={locked}
                    type="number"
                    step="0.5"
                    min="0"
                    className="rounded-sm bg-secondary/40 h-7 w-16 text-xs"
                    value={form.service_fee_pct ?? ""}
                    onChange={(e) => setForm({ ...form, service_fee_pct: e.target.value === "" ? 0 : parseFloat(e.target.value) })}
                    data-testid="quote-service-fee-pct"
                    title="Overrides the division default for this quote"
                  />
                  <span className="text-xs">%</span>
                </div>
                <div>{fmt(totals.fee, form.currency)}</div>
              </div>
              <Row label={`GST (${((form.gst_rate || 0) * 100).toFixed(1)}%)`} value={fmt(totals.gst, form.currency)} />
              <div className="h-px bg-border my-2" />
              <Row label="Total quoted" value={fmt(totals.total, form.currency)} bold />
            </div>
          </Card>

          {!isNew && !locked && (
            <Card className="rounded-sm bg-card border-border p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3">Quick status</div>
              <div className="grid grid-cols-2 gap-2">
                {["draft", "sent", "expired"].map((s) => (
                  <Button key={s} variant={form.status === s ? "default" : "outline"} size="sm" className="rounded-sm" onClick={() => setStatusMut.mutate(s)} data-testid={`quote-set-${s}`}>{STATUS_META[s]?.label}</Button>
                ))}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-3">
                Customer accepts via the public link → auto-converts to invoice.
              </div>
            </Card>
          )}

          {existing?.email_events?.length ? (
            <Card className="rounded-sm bg-card border-border p-6" data-testid="quote-email-events">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3">Email delivery</div>
              <div className="space-y-2">
                {(existing.email_events || []).slice().reverse().map((ev) => (
                  <div key={ev.id} className="text-xs flex items-start gap-2">
                    <Mail className="h-3.5 w-3.5 mt-0.5 text-primary" />
                    <div className="flex-1">
                      <div className="uppercase tracking-widest text-[9px] text-muted-foreground">{ev.event}</div>
                      <div className="truncate">{ev.to} · {new Date(ev.at).toLocaleString()}</div>
                      {ev.reason && <div className="text-red-400 text-[10px]">{ev.reason}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <div className={`text-muted-foreground ${bold ? "text-foreground font-semibold" : ""}`}>{label}</div>
      <div className={`${bold ? "font-black text-lg" : ""}`}>{value}</div>
    </div>
  );
}

function SendQuotationButton({ quoteId, defaultTo, status }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ to: defaultTo || "", cc: "", subject: "", message: "" });
  const qc = useQueryClient();
  useEffect(() => { setForm((f) => ({ ...f, to: defaultTo || f.to })); }, [defaultTo]);

  const send = useMutation({
    mutationFn: () => api.post(`/quotations/${quoteId}/send`, {
      to: form.to,
      cc: form.cc ? form.cc.split(",").map((s) => s.trim()).filter(Boolean) : null,
      subject: form.subject || null,
      message: form.message || null,
    }),
    onSuccess: () => {
      setOpen(false);
      toast.success(`Quotation emailed to ${form.to}`);
      qc.invalidateQueries({ queryKey: ["quotation", quoteId] });
    },
    onError: (e) => {
      const detail = e.response?.data?.detail || "";
      if (/smtp_not_configured/i.test(detail)) {
        toast.error("Your mailbox isn't set up yet", {
          description: "Add your SMTP mailbox in Settings → Email so LeadifyCRM can send quotations on your behalf.",
          action: { label: "Open Settings", onClick: () => window.location.assign("/settings?tab=email") },
          duration: 8000,
        });
      } else {
        toast.error(detail || "Send failed");
      }
      qc.invalidateQueries({ queryKey: ["quotation", quoteId] });
    },
  });

  return (
    <>
      <Button variant="outline" className="rounded-sm" onClick={() => setOpen(true)} data-testid="quote-email-btn">
        <Mail className="h-4 w-4 mr-2" />{status === "draft" ? "Email quote" : "Resend quote"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="email-quote-dialog">
          <DialogHeader><DialogTitle>Email Quotation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>To</Label><Input type="email" className="rounded-sm mt-1.5 bg-secondary/40" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} data-testid="quote-email-to" /></div>
            <div><Label>Cc <span className="text-[10px] text-muted-foreground uppercase tracking-widest">— comma-separated, optional</span></Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={form.cc} onChange={(e) => setForm({ ...form, cc: e.target.value })} /></div>
            <div><Label>Subject <span className="text-[10px] text-muted-foreground uppercase tracking-widest">— optional</span></Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
            <div><Label>Message <span className="text-[10px] text-muted-foreground uppercase tracking-widest">— optional</span></Label><Textarea rows={5} className="rounded-sm mt-1.5 bg-secondary/40" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground p-2 rounded-sm bg-secondary/30 border border-border">
              Sent from your SMTP mailbox · Public accept link included · PDF attached
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-sm bg-primary" onClick={() => send.mutate()} disabled={!form.to || send.isPending} data-testid="quote-email-send">
              {send.isPending ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
